import type { Signal } from "@preact/signals-core";
import { computed, signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, bindShow, bindText, bindValue, html, list } from "../lib/bind";
import { formatDate } from "../lib/date";
import { bindCheckbox, bindSelect, setSelectOptions } from "../lib/formBind";
import { form } from "../lib/form";
import { icon } from "../lib/icons";
import {
  formatMoney,
  multiplyMoney,
  parseMoneyInput,
  percentOf,
  subtractMoney,
  sumMoney,
} from "../lib/money";
import { currentScope } from "../lib/scope";
import { labelRow } from "../lib/table";
import { toast } from "../lib/toast";
import { registerUnsavedGuard } from "../lib/unsavedGuard";
import { type Client, getClient, listClients } from "../services/client";
import {
  type EmailLog,
  type ReminderEligibility,
  type Invoice,
  type InvoiceLineInput,
  type InvoiceStatus,
  type InvoiceSummary,
  type IssueOptions,
  type Payment,
  type PaymentMethod,
  createInvoice,
  createPayment,
  deleteInvoice,
  deletePayment,
  duplicateInvoice,
  getInvoice,
  invoicePdfUrl,
  invoicePreviewPdfUrl,
  invoiceTimesheetPreviewPdfUrl,
  invoiceTimesheetPdfUrl,
  issueInvoice,
  listInvoiceEmails,
  listInvoices,
  regenerateInvoicePdf,
  regenerateInvoiceTimesheetPdf,
  removeInvoicePdfUpload,
  retryInvoiceSend,
  uploadInvoicePdf,
  getReminderEligibility,
  sendInvoice,
  sendInvoiceReminder,
  updateInvoice,
  updateInvoiceTimesheetEntries,
  updatePayment,
  setInvoiceVatTreatment,
  voidInvoice,
} from "../services/invoice";
import { getCompany } from "../services/company";
import { type Project, type RateUnit, listProjects } from "../services/project";
import { type TimesheetEntry, listClientTimesheetEntries } from "../services/timesheet";
import { breadcrumb } from "../stores/breadcrumb";
import {
  type VatTreatment,
  VAT_TREATMENT_LABELS,
  ZERO_VAT_TREATMENTS,
} from "../services/vat";

// Typed rather than an inline `as` on the form literal: the form helper infers each
// field's type from this object, and a bare "standard" would widen it to string.
const DEFAULT_VAT_TREATMENT: VatTreatment = "standard";

/** A rated treatment alongside a nil rate is the one combination the server refuses
 * (ck_invoice_vat_treatment_rate). It arises legitimately — a company that isn't VAT
 * registered has a 0% default rate while the selector still reads "Standard" — so send
 * null there and let the server infer, rather than blocking the save. */
function vatTreatmentForSave(treatment: VatTreatment, rate: string): VatTreatment | null {
  if (!ZERO_VAT_TREATMENTS.has(treatment) && Number(rate || "0") === 0) return null;
  return treatment;
}
import { session } from "../stores/session";
import { periodLabel } from "./timesheets";

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: "Draft",
  issued: "Issued",
  sent: "Sent",
  part_paid: "Part paid",
  paid: "Paid",
  overdue: "Overdue",
  void: "Void",
  written_off: "Written off",
};

const STATUS_BADGE_CLASS: Record<InvoiceStatus, string> = {
  draft: "badge-neutral",
  issued: "badge-neutral",
  sent: "badge-neutral",
  part_paid: "badge-warn",
  paid: "badge-ok",
  overdue: "badge-error",
  void: "badge-neutral",
  written_off: "badge-error",
};

function statusLabel(status: InvoiceStatus): string {
  return STATUS_LABELS[status];
}

function statusBadgeClass(status: InvoiceStatus): string {
  return STATUS_BADGE_CLASS[status];
}

/** The document the invoice actually has — generated, or uploaded for a historical one;
 * `pdf_path` holds either, so this column needs to know nothing about which.
 *
 * Blank when there is none. After the Phase 8c upload work that blank is precisely the
 * set of imported invoices still waiting for their real document, readable at a glance
 * down the column — so it keeps the icon's width rather than collapsing and jogging every
 * invoice number left. */
function invoicePdfCell(inv: InvoiceSummary): Node {
  if (!inv.pdf_path) return html`<span class="pdf-icon-blank" aria-hidden="true"></span>`;

  const label = inv.pdf_uploaded_at ? "Uploaded document" : "Generated PDF";
  return html`<a
    class="pdf-icon-link"
    href="${invoicePdfUrl(inv.id)}"
    target="_blank"
    rel="noopener"
    title="${label}"
    aria-label="${label}"
    >${icon("pdf", "table-icon")}</a
  >`;
}

const INVOICES_PAGE_SIZE = 50;

export function invoicesListView(): Node {
  const invoices = signal<InvoiceSummary[]>([]);
  const clients = signal<Client[]>([]);
  const loadError = signal("");

  const statusFilter = signal("");
  const clientFilter = signal("");
  const dateFromFilter = signal("");
  const dateToFilter = signal("");
  const qFilter = signal("");
  const page = signal(1);
  const total = signal(0);

  function reload(): void {
    listInvoices({
      status: (statusFilter.value || undefined) as InvoiceStatus | undefined,
      client_id: clientFilter.value || undefined,
      date_from: dateFromFilter.value || undefined,
      date_to: dateToFilter.value || undefined,
      q: qFilter.value || undefined,
      page: page.value,
      page_size: INVOICES_PAGE_SIZE,
    })
      .then((result) => {
        invoices.value = result.items;
        total.value = result.total;
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }

  function filtersChanged(): void {
    page.value = 1;
    reload();
  }

  reload();
  listClients()
    .then((result) => {
      clients.value = result;
    })
    .catch(() => {
      // Client list failed to load; the filter dropdown just stays empty — non-fatal.
    });

  // Integer-pence arithmetic only (Phase 2a item 14) — float sums can drift a penny.
  const totals = computed(() => {
    const invoiceTotals = invoices.value.map((inv) => inv.total);
    const paid = invoices.value.map((inv) => inv.amount_paid);
    return {
      total: sumMoney(invoiceTotals),
      outstanding: subtractMoney(sumMoney(invoiceTotals), sumMoney(paid)),
    };
  });

  const fragment = html`<div class="view">
    <div class="card filters-card">
      <div class="form-row">
        <div class="form-field">
          <label for="inv-filter-status">Status</label>
          <select id="inv-filter-status">
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="issued">Issued</option>
            <option value="sent">Sent</option>
            <option value="part_paid">Part paid</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="void">Void</option>
            <option value="written_off">Written off</option>
          </select>
        </div>
        <div class="form-field">
          <label for="inv-filter-client">Client</label>
          <select id="inv-filter-client" class="client-filter-select">
            <option value="">All</option>
          </select>
        </div>
        <div class="form-field">
          <label for="inv-filter-from">From</label>
          <input id="inv-filter-from" type="date" />
        </div>
        <div class="form-field">
          <label for="inv-filter-to">To</label>
          <input id="inv-filter-to" type="date" />
        </div>
        <div class="form-field">
          <label for="inv-filter-q">Search (number/PO)</label>
          <input id="inv-filter-q" placeholder="e.g. ACME-2026-3" />
        </div>
      </div>
    </div>

    <p class="form-error"></p>

    <div class="card">
      <div class="card-label-row">
        <div class="card-label">Invoices</div>
        <div class="card-label-actions">
          <a href="/invoices/import" data-link class="btn import-invoices-btn">Import invoices</a>
          <a href="/invoices/new" data-link class="btn btn-primary">Add invoice</a>
        </div>
      </div>
      <div class="data-table">
        <table>
          <thead>
            <tr>
              <th>Number</th>
              <th>Client</th>
              <th>Issue date</th>
              <th>Due date</th>
              <th>Total</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody class="invoices-body"></tbody>
        </table>
      </div>

      <div class="pager">
        <button class="btn pager-prev" type="button">Previous</button>
        <span class="pager-label"></span>
        <button class="btn pager-next" type="button">Next</button>
      </div>
    </div>

    <div class="card invoices-footer">
      <div class="form-row">
        <div class="form-field">
          <span class="card-label">Total (filtered)</span>
          <span class="card-value totals-total"></span>
        </div>
        <div class="form-field">
          <span class="card-label">Outstanding (filtered)</span>
          <span class="card-value totals-outstanding"></span>
        </div>
      </div>
    </div>
  </div>`;

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const table = fragment.querySelector<HTMLTableElement>(".data-table table");
  const tbody = fragment.querySelector<HTMLTableSectionElement>(".invoices-body");
  const statusSelect = fragment.querySelector<HTMLSelectElement>("#inv-filter-status");
  const clientSelect = fragment.querySelector<HTMLSelectElement>(".client-filter-select");
  const fromInput = fragment.querySelector<HTMLInputElement>("#inv-filter-from");
  const toInput = fragment.querySelector<HTMLInputElement>("#inv-filter-to");
  const qInput = fragment.querySelector<HTMLInputElement>("#inv-filter-q");
  const totalsTotalEl = fragment.querySelector<HTMLElement>(".totals-total");
  const totalsOutstandingEl = fragment.querySelector<HTMLElement>(".totals-outstanding");
  const pagerEl = fragment.querySelector<HTMLElement>(".pager");
  const pagerPrevBtn = fragment.querySelector<HTMLButtonElement>(".pager-prev");
  const pagerNextBtn = fragment.querySelector<HTMLButtonElement>(".pager-next");
  const pagerLabelEl = fragment.querySelector<HTMLElement>(".pager-label");

  if (
    !errorEl ||
    !table ||
    !tbody ||
    !statusSelect ||
    !clientSelect ||
    !fromInput ||
    !toInput ||
    !qInput ||
    !totalsTotalEl ||
    !totalsOutstandingEl ||
    !pagerEl ||
    !pagerPrevBtn ||
    !pagerNextBtn ||
    !pagerLabelEl
  ) {
    throw new Error("invoices list markup missing expected elements");
  }

  bindErrorText(errorEl, loadError);

  currentScope().effect(() => {
    setSelectOptions(
      clientSelect,
      clients.value.map((c) => ({ value: c.id, label: c.name })),
      { placeholder: "All" },
    );
    clientSelect.value = clientFilter.value;
  });

  statusSelect.addEventListener("change", () => {
    statusFilter.value = statusSelect.value;
    filtersChanged();
  });
  clientSelect.addEventListener("change", () => {
    clientFilter.value = clientSelect.value;
    filtersChanged();
  });
  fromInput.addEventListener("change", () => {
    dateFromFilter.value = fromInput.value;
    filtersChanged();
  });
  toInput.addEventListener("change", () => {
    dateToFilter.value = toInput.value;
    filtersChanged();
  });
  let searchDebounce: ReturnType<typeof setTimeout> | undefined;
  qInput.addEventListener("input", () => {
    qFilter.value = qInput.value;
    clearTimeout(searchDebounce);
    searchDebounce = setTimeout(filtersChanged, 300);
  });

  const pageCount = computed(() => Math.max(1, Math.ceil(total.value / INVOICES_PAGE_SIZE)));
  currentScope().effect(() => {
    pagerEl.style.display = pageCount.value > 1 ? "" : "none";
    pagerLabelEl.textContent = `Page ${String(page.value)} of ${String(pageCount.value)}`;
    pagerPrevBtn.disabled = page.value <= 1;
    pagerNextBtn.disabled = page.value >= pageCount.value;
  });
  pagerPrevBtn.addEventListener("click", () => {
    if (page.value > 1) {
      page.value -= 1;
      reload();
    }
  });
  pagerNextBtn.addEventListener("click", () => {
    if (page.value < pageCount.value) {
      page.value += 1;
      reload();
    }
  });

  currentScope().effect(() => {
    totalsTotalEl.textContent = formatMoney(totals.value.total);
    totalsOutstandingEl.textContent = formatMoney(totals.value.outstanding);
  });

  list(
    tbody,
    invoices,
    (inv) => inv.id,
    (inv) => {
      const href = inv.status === "draft" ? `/invoices/${inv.id}` : `/invoices/${inv.id}/view`;
      const row = html`<tr>
        <td>
          <span class="invoice-number-cell"
            >${invoicePdfCell(inv)}<a href="${href}" data-link class="invoice-number-link"
              >${inv.number ?? "(draft)"}</a
            ></span
          >
        </td>
        <td>${inv.client_name}</td>
        <td>${inv.issue_date}</td>
        <td>${inv.due_date}</td>
        <td>${formatMoney(inv.total, inv.currency)}</td>
        <td>
          <span class="badge ${statusBadgeClass(inv.status)}">${statusLabel(inv.status)}</span>
          ${inv.kind === "credit_note" ? html`<span class="badge badge-neutral">Credit note</span>` : ""}
          ${inv.is_backdated ? html`<span class="badge badge-neutral">Historical</span>` : ""}
          ${inv.days_overdue > 0 ? html`<span class="badge badge-error">${String(inv.days_overdue)}d overdue</span>` : ""}
        </td>
        <td><a href="${href}" data-link class="btn">${inv.status === "draft" ? "Edit" : "View"}</a></td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("invoice row template produced no element");
      return labelRow(row, table);
    },
  );

  return fragment;
}

interface LineRow {
  key: string;
  projectId: Signal<string>;
  description: Signal<string>;
  quantity: Signal<string>;
  unitPrice: Signal<string>;
  unit: Signal<RateUnit>;
}

let lineRowSeq = 0;

function newLineRow(initial?: Partial<InvoiceLineInput>): LineRow {
  lineRowSeq += 1;
  return {
    key: `line-${lineRowSeq}`,
    projectId: signal(initial?.project_id ?? ""),
    description: signal(initial?.description ?? ""),
    quantity: signal(initial?.quantity ?? "1"),
    unitPrice: signal(initial?.unit_price ?? "0.00"),
    unit: signal(initial?.unit ?? "item"),
  };
}

function lineRowToInput(row: LineRow): InvoiceLineInput {
  return {
    project_id: row.projectId.value || null,
    description: row.description.value,
    quantity: row.quantity.value,
    unit_price: row.unitPrice.value,
    unit: row.unit.value,
  };
}

function lineRowAmount(row: LineRow): string {
  return multiplyMoney(row.quantity.value, row.unitPrice.value);
}

function buildLineRow(
  row: LineRow,
  projects: Signal<Project[]>,
  onRemove: (key: string) => void,
): Element {
  const tr = html`<tr>
    <td><select class="line-project"></select></td>
    <td><input class="line-description" placeholder="Description" /></td>
    <td><input class="line-quantity" inputmode="decimal" /></td>
    <td><input class="line-unit-price" inputmode="decimal" /></td>
    <td>
      <select class="line-unit">
        <option value="hour">Hour</option>
        <option value="day">Day</option>
        <option value="fixed">Fixed</option>
        <option value="item">Item</option>
      </select>
    </td>
    <td class="line-amount-display"></td>
    <td><button class="btn btn-danger remove-line-btn" type="button">Remove</button></td>
  </tr>`.firstElementChild;
  if (!tr) throw new Error("invoice line row template produced no element");

  const projectSelect = tr.querySelector<HTMLSelectElement>(".line-project");
  const descriptionInput = tr.querySelector<HTMLInputElement>(".line-description");
  const quantityInput = tr.querySelector<HTMLInputElement>(".line-quantity");
  const unitPriceInput = tr.querySelector<HTMLInputElement>(".line-unit-price");
  const unitSelect = tr.querySelector<HTMLSelectElement>(".line-unit");
  const amountDisplay = tr.querySelector<HTMLElement>(".line-amount-display");
  const removeBtn = tr.querySelector<HTMLButtonElement>(".remove-line-btn");

  if (
    !projectSelect ||
    !descriptionInput ||
    !quantityInput ||
    !unitPriceInput ||
    !unitSelect ||
    !amountDisplay ||
    !removeBtn
  ) {
    throw new Error("invoice line row markup missing expected elements");
  }

  currentScope().effect(() => {
    setSelectOptions(
      projectSelect,
      projects.value.map((p) => ({ value: p.id, label: p.name })),
      { placeholder: "—" },
    );
    projectSelect.value = row.projectId.value;
  });

  // Suggests "{name} ({code})" as the description when a project is picked, but only
  // while the field is still empty or still holds a prior suggestion — a genuine
  // keystroke (see the "input" listener below) permanently opts this row out, so
  // picking a different project later never clobbers text the user typed themselves.
  let descriptionAutoFilled = row.description.value.trim() === "";
  descriptionInput.addEventListener("input", () => {
    descriptionAutoFilled = false;
  });

  projectSelect.addEventListener("change", () => {
    row.projectId.value = projectSelect.value;
    if (!projectSelect.value || !descriptionAutoFilled) return;
    const project = projects.value.find((p) => p.id === projectSelect.value);
    if (!project) return;
    row.description.value = project.code ? `${project.name} (${project.code})` : project.name;
    descriptionAutoFilled = true;
  });

  bindValue(descriptionInput, row.description);
  bindValue(quantityInput, row.quantity);
  bindValue(unitPriceInput, row.unitPrice, {
    format: (v) => v,
    parse: (v) => parseMoneyInput(v) ?? v,
  });
  bindSelect(unitSelect, row.unit);

  currentScope().effect(() => {
    amountDisplay.textContent = formatMoney(lineRowAmount(row));
  });

  removeBtn.addEventListener("click", () => onRemove(row.key));

  return tr;
}

export function invoiceEditorView(params: Record<string, string>): Node {
  const paramId = params.id ?? "";
  const isNew = paramId === "new";
  const invoiceId = signal(isNew ? "" : paramId);
  const savedAt = signal("");
  const errorMessage = signal("");

  breadcrumb.value = { label: "Invoices", path: "/invoices" };
  currentScope().cleanup(() => {
    breadcrumb.value = null;
  });

  const today = new Date().toISOString().slice(0, 10);
  const headerForm = form({
    client_id: "",
    issue_date: today,
    due_date: "",
    currency: "GBP",
    vat_rate: "20.00",
    vat_treatment: DEFAULT_VAT_TREATMENT,
    po_reference: "",
    notes_public: "",
    notes_internal: "",
  });

  const clients = signal<Client[]>([]);
  const projectsForClient = signal<Project[]>([]);
  const lineRows = signal<LineRow[]>([]);
  const companyDefaultVatRate = signal<string | null>(null);
  // Historical/backdated entry (re-entering past accounts from another system) — a
  // custom number that skips the normal auto-numbered sequence entirely.
  const backdated = signal(false);
  const backdatedNumber = signal("");
  // Credit note (Phase 5): set once at creation only, so this toggle is hidden once
  // the draft already exists (services/invoice.ts's InvoiceCreateInput.kind).
  const creditNote = signal(false);
  const timesheetEntryCount = signal(0);
  const timesheetFrom = signal<string | null>(null);
  const timesheetTo = signal<string | null>(null);

  listClients()
    .then((result) => {
      clients.value = result;
    })
    .catch(() => {
      // Client list failed to load; the picker just stays empty — non-fatal.
    });
  const activeCompanyId = session.companyId.value;
  if (activeCompanyId) {
    getCompany(activeCompanyId)
      .then((company) => {
        companyDefaultVatRate.value = company.default_vat_rate;
        // A 0% default means this company doesn't charge VAT; showing "Standard" next
        // to a nil rate would misdescribe every invoice it creates.
        if (Number(company.default_vat_rate ?? "0") === 0) {
          headerForm.fields.vat_treatment.value = "zero_rated";
        }
      })
      .catch(() => {
        companyDefaultVatRate.value = null;
      });
  }

  // Unsaved-changes guard (Phase 2a item 11): header form dirtiness comes from form(),
  // line dirtiness from comparing a serialized snapshot taken at load/save.
  let linesBaseline = JSON.stringify([] as InvoiceLineInput[]);
  let leavingClean = false;
  function serializeLines(): string {
    return JSON.stringify(lineRows.value.map(lineRowToInput));
  }
  registerUnsavedGuard(
    () => !leavingClean && (headerForm.dirty.value || serializeLines() !== linesBaseline),
  );

  currentScope().effect(() => {
    const clientId = headerForm.fields.client_id.value;
    if (!clientId) {
      projectsForClient.value = [];
      return;
    }
    listProjects(clientId)
      .then((result) => {
        projectsForClient.value = result.filter((p) => p.active);
      })
      .catch(() => {
        projectsForClient.value = [];
      });
  });

  function applyTimesheetSummary(invoice: Invoice): void {
    timesheetEntryCount.value = invoice.timesheet_entry_count;
    timesheetFrom.value = invoice.timesheet_from;
    timesheetTo.value = invoice.timesheet_to;
  }

  function applyLoadedInvoice(invoice: Invoice): void {
    headerForm.reset({
      client_id: invoice.client_id,
      issue_date: invoice.issue_date,
      due_date: invoice.due_date,
      currency: invoice.currency,
      vat_rate: invoice.vat_rate,
      vat_treatment: invoice.vat_treatment,
      po_reference: invoice.po_reference ?? "",
      notes_public: invoice.notes_public ?? "",
      notes_internal: invoice.notes_internal ?? "",
    });
    savedAt.value = invoice.updated_at;
    applyTimesheetSummary(invoice);
    lineRows.value = invoice.lines.map((l) =>
      newLineRow({
        project_id: l.project_id,
        description: l.description,
        quantity: l.quantity,
        unit_price: l.unit_price,
        unit: l.unit,
      }),
    );
    linesBaseline = serializeLines();
  }

  if (!isNew) {
    getInvoice(paramId)
      .then((invoice) => {
        if (invoice.status !== "draft") {
          location.assign(`/invoices/${invoice.id}/view`);
          return;
        }
        applyLoadedInvoice(invoice);
      })
      .catch((err: unknown) => {
        errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }

  // "Pull from timesheet" modal (04-frontend.md invoice editor): date range (default
  // since the client's last invoice), grouped preview of uninvoiced entries, confirm →
  // PUT .../timesheet-entries with rollup=true regenerates this draft's lines from the
  // selected set and attaches them (invoice_service.update_timesheet_entries).
  const pullDialogOpen = signal(false);
  const pullDateFrom = signal("");
  const pullDateTo = signal("");
  const pullEntries = signal<TimesheetEntry[]>([]);
  const pullSelectedIds = signal<Set<string>>(new Set());
  const pullError = signal("");

  const pullGroups = computed(() => {
    const groups: { projectName: string; entries: TimesheetEntry[] }[] = [];
    const byProject = new Map<string, { projectName: string; entries: TimesheetEntry[] }>();
    for (const entry of pullEntries.value) {
      let group = byProject.get(entry.project_id);
      if (!group) {
        group = { projectName: entry.project_name, entries: [] };
        byProject.set(entry.project_id, group);
        groups.push(group);
      }
      group.entries.push(entry);
    }
    return groups;
  });

  async function reloadPullPreview(): Promise<void> {
    const clientId = headerForm.fields.client_id.value;
    if (!clientId || !pullDateFrom.value || !pullDateTo.value) {
      pullEntries.value = [];
      return;
    }
    try {
      const result = await listClientTimesheetEntries(clientId, {
        date_from: pullDateFrom.value,
        date_to: pullDateTo.value,
        uninvoiced: true,
        page_size: 200,
      });
      // Selected-ids first: the rendering effect depends only on pullGroups (derived
      // from pullEntries) and reads pullSelectedIds untracked at creation time — this
      // order makes sure it seeds from the fresh "select all" set, not last time's.
      pullSelectedIds.value = new Set(result.items.map((e) => e.id));
      pullEntries.value = result.items;
    } catch (err) {
      pullError.value =
        err instanceof ApiRequestError ? err.body.message : "Failed to load entries";
      pullEntries.value = [];
    }
  }

  async function openPullFromTimesheet(): Promise<void> {
    if (!invoiceId.value) {
      toast.error("Save the invoice as a draft before pulling from timesheet.");
      return;
    }
    const clientId = headerForm.fields.client_id.value;
    if (!clientId) {
      toast.error("Select a client first.");
      return;
    }
    pullError.value = "";
    const today = new Date();
    let fromDate = new Date(today);
    fromDate.setDate(fromDate.getDate() - 30);
    try {
      // "Default since client's last invoice" (04-frontend.md); falls back to 30 days
      // when the client has none yet (listInvoices is already ordered issue_date desc).
      const last = await listInvoices({ client_id: clientId, page: 1, page_size: 1 });
      const lastIssueDate = last.items[0]?.issue_date;
      if (lastIssueDate) {
        fromDate = new Date(lastIssueDate);
        fromDate.setDate(fromDate.getDate() + 1);
      }
    } catch {
      // Falls back to the 30-day default set above — non-fatal.
    }
    pullDateFrom.value = fromDate.toISOString().slice(0, 10);
    pullDateTo.value = today.toISOString().slice(0, 10);
    await reloadPullPreview();
    pullDialogOpen.value = true;
  }

  function toggleEntrySelected(id: string, checked: boolean): void {
    const next = new Set(pullSelectedIds.value);
    if (checked) next.add(id);
    else next.delete(id);
    pullSelectedIds.value = next;
  }

  function closePullDialog(): void {
    pullDialogOpen.value = false;
  }

  async function confirmPullFromTimesheet(): Promise<void> {
    if (!invoiceId.value) return;
    pullError.value = "";
    try {
      const updated = await updateInvoiceTimesheetEntries(invoiceId.value, {
        entry_ids: [...pullSelectedIds.value],
        rollup: true,
      });
      applyLoadedInvoice(updated);
      closePullDialog();
      toast.success("Timesheet entries pulled in.");
    } catch (err) {
      // Shown inline in the (still-open) dialog, not just a toast — e.g. a
      // currency_mismatch 422 needs to stay attached to the modal the user is looking
      // at, since nothing was created (invoice_service.build_rollup_lines rejects
      // wholesale before any mutation).
      const message =
        err instanceof ApiRequestError ? err.body.message : "Failed to pull from timesheet";
      pullError.value = message;
      toast.error(message);
    }
  }

  // Mirrors the server rule in integer pence: line totals half-up, VAT once on the
  // subtotal — never floats (Phase 2a item 14).
  const previewTotals = computed(() => {
    const subtotal = sumMoney(lineRows.value.map(lineRowAmount));
    const vatAmount = percentOf(subtotal, headerForm.fields.vat_rate.value || "0");
    return { subtotal, vatAmount, total: sumMoney([subtotal, vatAmount]) };
  });

  function addLine(): void {
    lineRows.value = [...lineRows.value, newLineRow()];
  }
  function removeLine(key: string): void {
    lineRows.value = lineRows.value.filter((r) => r.key !== key);
  }

  async function save(event: Event): Promise<void> {
    event.preventDefault();
    errorMessage.value = "";
    const lines = lineRows.value.map(lineRowToInput);
    try {
      if (isNew || !invoiceId.value) {
        const created = await createInvoice({
          client_id: headerForm.fields.client_id.value,
          issue_date: headerForm.fields.issue_date.value || null,
          due_date: headerForm.fields.due_date.value || null,
          currency: headerForm.fields.currency.value,
          vat_rate: headerForm.fields.vat_rate.value,
          vat_treatment: vatTreatmentForSave(
            headerForm.fields.vat_treatment.value,
            headerForm.fields.vat_rate.value,
          ),
          po_reference: headerForm.fields.po_reference.value || null,
          notes_public: headerForm.fields.notes_public.value || null,
          notes_internal: headerForm.fields.notes_internal.value || null,
          lines,
          kind: creditNote.value ? "credit_note" : "invoice",
        });
        toast.queueSuccess("Invoice created.");
        leavingClean = true;
        location.assign(`/invoices/${created.id}`);
        return;
      }
      const updated = await updateInvoice(invoiceId.value, {
        client_id: headerForm.fields.client_id.value,
        issue_date: headerForm.fields.issue_date.value,
        due_date: headerForm.fields.due_date.value,
        currency: headerForm.fields.currency.value,
        vat_rate: headerForm.fields.vat_rate.value,
        vat_treatment: vatTreatmentForSave(
          headerForm.fields.vat_treatment.value,
          headerForm.fields.vat_rate.value,
        ),
        po_reference: headerForm.fields.po_reference.value || null,
        notes_public: headerForm.fields.notes_public.value || null,
        notes_internal: headerForm.fields.notes_internal.value || null,
        lines,
        updated_at: savedAt.value,
      });
      savedAt.value = updated.updated_at;
      applyTimesheetSummary(updated);
      headerForm.reset(headerForm.values());
      linesBaseline = serializeLines();
      toast.success("Invoice saved.");
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 412) {
        errorMessage.value = "This invoice changed elsewhere — reload and retry.";
      } else {
        errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Save failed";
      }
      toast.error(errorMessage.value);
    }
  }

  async function doIssue(): Promise<void> {
    if (!invoiceId.value) {
      toast.error("Save the invoice as a draft before issuing it.");
      return;
    }
    if (lineRows.value.length === 0) {
      toast.error("Add at least one line before issuing.");
      return;
    }
    if (headerForm.dirty.value || serializeLines() !== linesBaseline) {
      toast.error("Save your changes before issuing.");
      return;
    }

    const options: IssueOptions = {};

    // fx prompt for non-GBP (Phase 2a item 8) — manual entry is the v1 mechanism.
    const currency = headerForm.fields.currency.value.trim().toUpperCase();
    if (currency !== "GBP") {
      const rate = window.prompt(
        `Exchange rate to GBP for ${currency} (1 ${currency} = ? GBP, e.g. 0.855):`,
      );
      if (rate === null) return;
      if (!/^\d+(\.\d{1,6})?$/.test(rate.trim()) || Number(rate) <= 0) {
        toast.error("Enter the rate as a plain positive number, e.g. 0.855");
        return;
      }
      options.fx_rate_to_gbp = rate.trim();
    }

    if (backdated.value) {
      // Historical entry: the issue date is honoured exactly as entered (no
      // "will be set to today" override) and paired with a custom number that skips
      // the normal auto-numbered sequence — for re-entering past accounts.
      const number = backdatedNumber.value.trim();
      if (!number) {
        toast.error("Enter a custom invoice number for a backdated invoice.");
        return;
      }
      if (!headerForm.fields.issue_date.value) {
        toast.error("Set an issue date for a backdated invoice.");
        return;
      }
      options.issue_date = headerForm.fields.issue_date.value;
      options.due_date = headerForm.fields.due_date.value;
      options.number = number;
      if (
        !window.confirm(
          `Issue this as a historical invoice numbered "${number}", dated ` +
            `${options.issue_date}? It will not consume the normal sequential numbering.`,
        )
      ) {
        return;
      }
    } else {
      // Issue stamps today (UK tax point). If the drafted issue date IS today the user
      // has effectively confirmed the dates — send them explicitly (keeps a hand-picked
      // due date); otherwise warn that today + payment terms will replace the draft's.
      const todayIso = new Date().toISOString().slice(0, 10);
      if (headerForm.fields.issue_date.value === todayIso) {
        options.issue_date = headerForm.fields.issue_date.value;
        options.due_date = headerForm.fields.due_date.value;
        if (!window.confirm("Issue this invoice? It will be assigned a permanent number.")) {
          return;
        }
      } else if (
        !window.confirm(
          `Issue this invoice? The draft is dated ${headerForm.fields.issue_date.value}; ` +
            `issuing will set the issue date to today (${todayIso}) and recompute the due ` +
            "date from the client's payment terms.",
        )
      ) {
        return;
      }
    }

    try {
      const issued = await issueInvoice(invoiceId.value, options);
      // Only one toast survives the reload (queue() holds a single pending slot) — the
      // closed-year warning is the more important thing to surface when both apply.
      if (issued.closed_year_warning) {
        toast.queueWarning(issued.closed_year_warning);
      } else {
        toast.queueSuccess(`Invoice ${issued.number} issued.`);
      }
      leavingClean = true;
      location.assign(`/invoices/${issued.id}/view`);
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Issue failed");
    }
  }

  function previewPdf(): void {
    if (!invoiceId.value) {
      toast.error("Save the draft first to preview its PDF.");
      return;
    }
    // Shows the last-saved state — the toast is a nudge to save again first if the
    // user has unsaved edits, rather than silently rendering a stale preview.
    window.open(invoicePreviewPdfUrl(invoiceId.value), "_blank", "noopener");
  }

  async function doDelete(): Promise<void> {
    if (!invoiceId.value) {
      location.assign("/invoices");
      return;
    }
    if (!window.confirm("Delete this draft invoice?")) return;
    try {
      await deleteInvoice(invoiceId.value);
      toast.queueSuccess("Draft deleted.");
      leavingClean = true;
      location.assign("/invoices");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Delete failed");
    }
  }

  const fragment = html`<div class="view">
    <div class="page-header">
      <h1>${isNew ? "New invoice" : "Edit invoice"}</h1>
    </div>
    <p class="form-error"></p>

    <form class="form invoice-header-form" onsubmit="${save}">
      <div class="checkbox-field credit-note-field">
        <input id="inv-credit-note" type="checkbox" />
        <label for="inv-credit-note">Credit note</label>
        <p class="field-hint">
          Shares this company's invoice numbering sequence; enter negative
          quantities/prices on the lines below.
        </p>
      </div>
      <div class="form-row">
        <div class="form-field">
          <label for="inv-client">Client</label>
          <select id="inv-client" required></select>
        </div>
        <div class="form-field">
          <label for="inv-issue-date">Issue date</label>
          <input id="inv-issue-date" type="date" required />
        </div>
        <div class="form-field">
          <label for="inv-due-date">Due date</label>
          <input id="inv-due-date" type="date" required />
        </div>
      </div>
      <div class="form-row">
        <div class="form-field">
          <label for="inv-currency">Currency</label>
          <input id="inv-currency" required />
        </div>
        <div class="form-field">
          <label for="inv-vat-treatment">VAT treatment</label>
          <select id="inv-vat-treatment">
            <option value="standard">Standard</option>
            <option value="reduced">Reduced</option>
            <option value="zero_rated">Zero-rated</option>
            <option value="exempt">Exempt</option>
            <option value="outside_scope">Outside scope of UK VAT</option>
          </select>
        </div>
        <div class="form-field">
          <label for="inv-vat-rate">VAT rate (%)</label>
          <input id="inv-vat-rate" inputmode="decimal" required />
        </div>
        <div class="form-field">
          <label for="inv-po-reference">PO reference</label>
          <input id="inv-po-reference" />
        </div>
      </div>
      <div class="form-field">
        <label for="inv-notes-public">Notes (printed on invoice)</label>
        <textarea id="inv-notes-public" rows="3"></textarea>
      </div>
      <div class="form-field">
        <label for="inv-notes-internal">Internal notes</label>
        <textarea id="inv-notes-internal" rows="2"></textarea>
      </div>

      <div class="card invoice-lines-card">
        <div class="page-header">
          <span class="card-label">Lines</span>
          <button class="btn add-line-btn" type="button">Add line</button>
        </div>
        <div class="data-table invoice-lines-table">
          <table>
            <thead>
              <tr>
                <th>Project</th>
                <th>Description</th>
                <th>Qty</th>
                <th>Unit price</th>
                <th>Unit</th>
                <th>Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody class="lines-body"></tbody>
          </table>
        </div>
        <table class="invoice-totals">
          <tr><td>Subtotal</td><td class="preview-subtotal"></td></tr>
          <tr><td>VAT</td><td class="preview-vat"></td></tr>
          <tr class="invoice-total-row"><td>Total</td><td class="preview-total"></td></tr>
        </table>
      </div>

      <div class="card draft-timesheet-card">
        <div class="page-header">
          <span class="card-label">Attached timesheet</span>
          <div class="invoice-actions">
            <a class="btn preview-timesheet-pdf-link" target="_blank" rel="noopener">Preview timesheet PDF</a>
          </div>
        </div>
        <div class="form-row">
          <div class="form-field">
            <span class="card-label">Entries</span>
            <span class="draft-timesheet-entry-count"></span>
          </div>
          <div class="form-field">
            <span class="card-label">Period</span>
            <span class="draft-timesheet-period"></span>
          </div>
        </div>
      </div>

      <div class="checkbox-field backdated-field">
        <input id="inv-backdated" type="checkbox" />
        <label for="inv-backdated">Backdated / historical invoice</label>
      </div>
      <div class="form-field backdated-number-field">
        <label for="inv-backdated-number">Custom invoice number</label>
        <input id="inv-backdated-number" placeholder="e.g. OLDSYS-2025-014" />
        <p class="field-hint">
          Issues with the issue date above exactly as entered and this number instead of
          the next auto-assigned one — for re-entering past invoices from another
          system. Doesn't consume the normal sequential numbering.
        </p>
      </div>

      <div class="form-actions">
        <button class="btn btn-primary" type="submit">Save draft</button>
        <button class="btn pull-timesheet-btn" type="button">Pull from timesheet</button>
        <button class="btn preview-pdf-btn" type="button">Preview PDF</button>
        <button class="btn issue-btn" type="button">Issue</button>
        <button class="btn btn-danger delete-btn" type="button">Delete draft</button>
      </div>
    </form>

    <dialog class="pull-timesheet-dialog">
      <div class="card-label">Pull from timesheet</div>
      <p class="pull-error form-error"></p>
      <div class="form-row">
        <div class="form-field">
          <label for="pull-from">From</label>
          <input id="pull-from" type="date" />
        </div>
        <div class="form-field">
          <label for="pull-to">To</label>
          <input id="pull-to" type="date" />
        </div>
      </div>
      <div class="pull-groups"></div>
      <p class="pull-empty">No uninvoiced timesheet entries in this range.</p>
      <div class="form-actions">
        <button class="btn btn-primary pull-confirm-btn" type="button">Pull selected entries</button>
        <button class="btn pull-cancel-btn" type="button">Cancel</button>
      </div>
    </dialog>
  </div>`;

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const clientSelect = fragment.querySelector<HTMLSelectElement>("#inv-client");
  const issueDateInput = fragment.querySelector<HTMLInputElement>("#inv-issue-date");
  const dueDateInput = fragment.querySelector<HTMLInputElement>("#inv-due-date");
  const currencyInput = fragment.querySelector<HTMLInputElement>("#inv-currency");
  const vatRateInput = fragment.querySelector<HTMLInputElement>("#inv-vat-rate");
  const vatTreatmentSelect = fragment.querySelector<HTMLSelectElement>("#inv-vat-treatment");
  const poReferenceInput = fragment.querySelector<HTMLInputElement>("#inv-po-reference");
  const notesPublicInput = fragment.querySelector<HTMLTextAreaElement>("#inv-notes-public");
  const notesInternalInput = fragment.querySelector<HTMLTextAreaElement>("#inv-notes-internal");
  const addLineBtn = fragment.querySelector<HTMLButtonElement>(".add-line-btn");
  const linesTable = fragment.querySelector<HTMLTableElement>(".invoice-lines-table table");
  const linesBody = fragment.querySelector<HTMLTableSectionElement>(".lines-body");
  const previewSubtotalEl = fragment.querySelector<HTMLElement>(".preview-subtotal");
  const previewVatEl = fragment.querySelector<HTMLElement>(".preview-vat");
  const previewTotalEl = fragment.querySelector<HTMLElement>(".preview-total");
  const previewPdfBtn = fragment.querySelector<HTMLButtonElement>(".preview-pdf-btn");
  const issueBtn = fragment.querySelector<HTMLButtonElement>(".issue-btn");
  const deleteBtn = fragment.querySelector<HTMLButtonElement>(".delete-btn");
  const pullTimesheetBtn = fragment.querySelector<HTMLButtonElement>(".pull-timesheet-btn");
  const pullDialogEl = fragment.querySelector<HTMLDialogElement>(".pull-timesheet-dialog");
  const pullErrorEl = fragment.querySelector<HTMLParagraphElement>(".pull-error");
  const pullFromInput = fragment.querySelector<HTMLInputElement>("#pull-from");
  const pullToInput = fragment.querySelector<HTMLInputElement>("#pull-to");
  const pullGroupsEl = fragment.querySelector<HTMLDivElement>(".pull-groups");
  const pullEmptyEl = fragment.querySelector<HTMLParagraphElement>(".pull-empty");
  const pullConfirmBtn = fragment.querySelector<HTMLButtonElement>(".pull-confirm-btn");
  const pullCancelBtn = fragment.querySelector<HTMLButtonElement>(".pull-cancel-btn");
  const backdatedInput = fragment.querySelector<HTMLInputElement>("#inv-backdated");
  const backdatedNumberField = fragment.querySelector<HTMLElement>(".backdated-number-field");
  const backdatedNumberInput = fragment.querySelector<HTMLInputElement>("#inv-backdated-number");
  const creditNoteField = fragment.querySelector<HTMLElement>(".credit-note-field");
  const creditNoteInput = fragment.querySelector<HTMLInputElement>("#inv-credit-note");
  const draftTimesheetCard = fragment.querySelector<HTMLElement>(".draft-timesheet-card");
  const draftTimesheetCountEl = fragment.querySelector<HTMLElement>(".draft-timesheet-entry-count");
  const draftTimesheetPeriodEl = fragment.querySelector<HTMLElement>(".draft-timesheet-period");
  const previewTimesheetPdfLink = fragment.querySelector<HTMLAnchorElement>(
    ".preview-timesheet-pdf-link",
  );

  if (
    !errorEl ||
    !clientSelect ||
    !issueDateInput ||
    !dueDateInput ||
    !currencyInput ||
    !vatRateInput ||
    !vatTreatmentSelect ||
    !poReferenceInput ||
    !notesPublicInput ||
    !notesInternalInput ||
    !addLineBtn ||
    !linesTable ||
    !linesBody ||
    !previewSubtotalEl ||
    !previewVatEl ||
    !previewTotalEl ||
    !previewPdfBtn ||
    !issueBtn ||
    !deleteBtn ||
    !pullTimesheetBtn ||
    !pullDialogEl ||
    !pullErrorEl ||
    !pullFromInput ||
    !pullToInput ||
    !pullGroupsEl ||
    !pullEmptyEl ||
    !pullConfirmBtn ||
    !pullCancelBtn ||
    !backdatedInput ||
    !backdatedNumberField ||
    !backdatedNumberInput ||
    !creditNoteField ||
    !creditNoteInput ||
    !draftTimesheetCard ||
    !draftTimesheetCountEl ||
    !draftTimesheetPeriodEl ||
    !previewTimesheetPdfLink
  ) {
    throw new Error("invoice editor markup missing expected elements");
  }

  // Only once entries are attached — the stored timesheet PDF is still made at issue;
  // this preview is rendered on request and never saved.
  bindShow(draftTimesheetCard, computed(() => timesheetEntryCount.value > 0));
  bindText(draftTimesheetCountEl, computed(() => String(timesheetEntryCount.value)));
  bindText(
    draftTimesheetPeriodEl,
    computed(() =>
      timesheetFrom.value && timesheetTo.value
        ? `${timesheetFrom.value} – ${timesheetTo.value}`
        : "—",
    ),
  );
  currentScope().effect(() => {
    previewTimesheetPdfLink.href = invoiceId.value
      ? invoiceTimesheetPreviewPdfUrl(invoiceId.value)
      : "#";
  });

  bindErrorText(errorEl, errorMessage);
  bindCheckbox(backdatedInput, backdated);
  bindValue(backdatedNumberInput, backdatedNumber);
  bindShow(backdatedNumberField, backdated);
  bindCheckbox(creditNoteInput, creditNote);
  // Set once at creation only — an existing (already-saved) invoice's kind is fixed.
  if (!isNew) creditNoteField.style.display = "none";

  currentScope().effect(() => {
    setSelectOptions(
      clientSelect,
      clients.value.map((c) => ({ value: c.id, label: c.name })),
      { placeholder: "Select a client…" },
    );
    clientSelect.value = headerForm.fields.client_id.value;
  });
  clientSelect.addEventListener("change", () => {
    headerForm.fields.client_id.value = clientSelect.value;
    // Prefill from the selected client / company defaults (Phase 2a item 7) — only on a
    // real user selection ("change" never fires for programmatic value sets, so loading
    // an existing draft can't clobber its saved values). Everything stays editable.
    const client = clients.value.find((c) => c.id === clientSelect.value);
    if (!client) return;
    headerForm.fields.currency.value = client.default_currency;
    const vatDefault = client.default_vat_rate ?? companyDefaultVatRate.value;
    if (vatDefault !== null) headerForm.fields.vat_rate.value = vatDefault;
    if (client.default_vat_treatment !== null) {
      headerForm.fields.vat_treatment.value = client.default_vat_treatment;
    }
    const issueDate = new Date(headerForm.fields.issue_date.value || Date.now());
    issueDate.setDate(issueDate.getDate() + client.payment_terms_days);
    headerForm.fields.due_date.value = issueDate.toISOString().slice(0, 10);
  });

  bindValue(issueDateInput, headerForm.fields.issue_date);
  bindValue(dueDateInput, headerForm.fields.due_date);
  bindValue(currencyInput, headerForm.fields.currency);
  bindValue(vatRateInput, headerForm.fields.vat_rate, {
    format: (v) => v,
    parse: (v) => parseMoneyInput(v) ?? v,
  });

  // A no-VAT treatment forces the rate to 0 and locks the field, so the pair can never be
  // sent inconsistent and the server's CHECK constraint is unreachable from the UI.
  currentScope().effect(() => {
    const treatment = headerForm.fields.vat_treatment.value;
    vatTreatmentSelect.value = treatment;
    const chargesNoVat = ZERO_VAT_TREATMENTS.has(treatment);
    vatRateInput.disabled = chargesNoVat;
    if (chargesNoVat && headerForm.fields.vat_rate.value !== "0.00") {
      headerForm.fields.vat_rate.value = "0.00";
    }
  });
  vatTreatmentSelect.addEventListener("change", () => {
    headerForm.fields.vat_treatment.value = vatTreatmentSelect.value as VatTreatment;
  });
  bindValue(poReferenceInput, headerForm.fields.po_reference);
  bindValue(notesPublicInput, headerForm.fields.notes_public);
  bindValue(notesInternalInput, headerForm.fields.notes_internal);

  addLineBtn.addEventListener("click", addLine);
  previewPdfBtn.addEventListener("click", previewPdf);
  issueBtn.addEventListener("click", () => {
    void doIssue();
  });
  deleteBtn.addEventListener("click", () => {
    void doDelete();
  });

  list(
    linesBody,
    lineRows,
    (row) => row.key,
    (row) => labelRow(buildLineRow(row, projectsForClient, removeLine), linesTable),
  );

  currentScope().effect(() => {
    previewSubtotalEl.textContent = formatMoney(previewTotals.value.subtotal);
    previewVatEl.textContent = formatMoney(previewTotals.value.vatAmount);
    previewTotalEl.textContent = formatMoney(previewTotals.value.total);
  });

  pullTimesheetBtn.addEventListener("click", () => {
    void openPullFromTimesheet();
  });
  pullCancelBtn.addEventListener("click", closePullDialog);
  pullConfirmBtn.addEventListener("click", () => {
    void confirmPullFromTimesheet();
  });
  bindValue(pullFromInput, pullDateFrom);
  bindValue(pullToInput, pullDateTo);
  pullFromInput.addEventListener("change", () => {
    void reloadPullPreview();
  });
  pullToInput.addEventListener("change", () => {
    void reloadPullPreview();
  });
  // Escape/backdrop-dismiss fires the dialog's own native "close" — keep the signal in
  // sync so a later programmatic open() call doesn't fight an already-closed element.
  pullDialogEl.addEventListener("close", () => {
    pullDialogOpen.value = false;
  });
  bindErrorText(pullErrorEl, pullError);
  currentScope().effect(() => {
    if (pullDialogOpen.value && !pullDialogEl.open) pullDialogEl.showModal();
    if (!pullDialogOpen.value && pullDialogEl.open) pullDialogEl.close();
  });
  // Depends only on pullGroups (i.e. pullEntries) — pullSelectedIds is read untracked
  // (.peek()) at creation time only. It must NOT be a dependency here: toggling one
  // checkbox writes pullSelectedIds, and if this effect depended on it too, every
  // toggle would tear down and rebuild every row (destroying the very checkbox the
  // user just clicked mid-interaction).
  currentScope().effect(() => {
    pullGroupsEl.replaceChildren();
    for (const group of pullGroups.value) {
      const groupEl = document.createElement("div");
      groupEl.className = "pull-group";
      const heading = document.createElement("div");
      heading.className = "card-label";
      heading.textContent = group.projectName;
      groupEl.appendChild(heading);
      for (const entry of group.entries) {
        const rowLabel = document.createElement("label");
        rowLabel.className = "pull-entry-row";
        const checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.checked = pullSelectedIds.peek().has(entry.id);
        checkbox.addEventListener("change", () => {
          toggleEntrySelected(entry.id, checkbox.checked);
        });
        rowLabel.appendChild(checkbox);
        const text = document.createElement("span");
        const converted =
          entry.converted_quantity !== null
            ? ` (${entry.converted_quantity} ${entry.rate_unit})`
            : "";
        text.textContent = `${entry.date} — ${entry.description} — ${periodLabel(entry)}${converted}`;
        rowLabel.appendChild(text);
        groupEl.appendChild(rowLabel);
      }
      pullGroupsEl.appendChild(groupEl);
    }
    pullEmptyEl.style.display = pullGroups.value.length === 0 ? "" : "none";
  });

  return fragment;
}

function paymentMethodLabel(method: PaymentMethod): string {
  return { bank_transfer: "Bank transfer", card: "Card", cash: "Cash", other: "Other" }[method];
}

const EMAIL_STATUS_LABELS: Record<EmailLog["status"], string> = {
  queued: "Queued",
  sent: "Sent",
  failed: "Failed",
};

const EMAIL_STATUS_BADGE_CLASS: Record<EmailLog["status"], string> = {
  queued: "badge-neutral",
  sent: "badge-ok",
  failed: "badge-error",
};

function buildEmailRow(email: EmailLog, onRetry: (emailLogId: string) => void): Element {
  const row = html`<tr>
    <td>${email.sent_at ? formatDate(email.sent_at) : "—"}</td>
    <td>${email.to_addresses.join(", ")}</td>
    <td>
      ${email.kind === "reminder" ? html`<span class="badge badge-neutral">Reminder</span> ` : ""}${email.subject}
    </td>
    <td>
      <span class="badge ${EMAIL_STATUS_BADGE_CLASS[email.status]}">${EMAIL_STATUS_LABELS[email.status]}</span>
      ${email.error ? html`<div class="email-error-note">${email.error}</div>` : ""}
    </td>
    <td>
      ${
        email.status === "failed"
          ? html`<button class="btn retry-email-btn" type="button">Retry</button>`
          : ""
      }
    </td>
  </tr>`.firstElementChild;
  if (!row) throw new Error("email row template produced no element");

  if (email.status === "failed") {
    const retryBtn = row.querySelector<HTMLButtonElement>(".retry-email-btn");
    retryBtn?.addEventListener("click", () => {
      void onRetry(email.id);
    });
  }

  return row;
}

function buildPaymentRow(
  payment: Payment,
  onChanged: (updated: Invoice) => void,
): Element {
  const editing = signal(false);
  const notEditing = computed(() => !editing.value);
  const editForm = form({
    date: payment.date,
    amount: payment.amount,
    method: payment.method,
    reference: payment.reference ?? "",
  });

  function startEdit(): void {
    editForm.reset({
      date: payment.date,
      amount: payment.amount,
      method: payment.method,
      reference: payment.reference ?? "",
    });
    editing.value = true;
  }
  function cancelEdit(): void {
    editing.value = false;
  }

  async function saveEdit(): Promise<void> {
    try {
      const updated = await updatePayment(payment.id, {
        date: editForm.fields.date.value,
        amount: editForm.fields.amount.value,
        method: editForm.fields.method.value,
        reference: editForm.fields.reference.value || null,
        updated_at: payment.updated_at,
      });
      // list() re-renders this row automatically — the response carries fresh payment
      // objects, and rows are keyed on item identity (Phase 2a item 13).
      onChanged(updated);
      toast.success("Payment updated.");
    } catch (err) {
      const message =
        err instanceof ApiRequestError && err.status === 412
          ? "This payment changed elsewhere — reload and retry."
          : err instanceof ApiRequestError
            ? err.body.message
            : "Update failed";
      toast.error(message);
    }
  }

  async function doDelete(): Promise<void> {
    if (!window.confirm("Delete this payment?")) return;
    try {
      const updated = await deletePayment(payment.id);
      onChanged(updated);
      toast.success("Payment deleted.");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Delete failed");
    }
  }

  const row = html`<tr>
    <td>
      <span class="payment-date-display">${payment.date}</span>
      <input class="payment-edit-date payment-edit-field" type="date" />
    </td>
    <td>
      <span class="payment-amount-display">${formatMoney(payment.amount)}</span>
      <input class="payment-edit-amount payment-edit-field" inputmode="decimal" />
    </td>
    <td>
      <span class="payment-method-display">${paymentMethodLabel(payment.method)}</span>
      <select class="payment-edit-method payment-edit-field">
        <option value="bank_transfer">Bank transfer</option>
        <option value="card">Card</option>
        <option value="cash">Cash</option>
        <option value="other">Other</option>
      </select>
    </td>
    <td>
      <span class="payment-reference-display">${payment.reference ?? ""}</span>
      <input class="payment-edit-reference payment-edit-field" placeholder="Reference" />
    </td>
    <td class="payment-actions">
      <button class="btn edit-payment-btn" type="button">Edit</button>
      <button class="btn btn-primary save-payment-btn" type="button">Save</button>
      <button class="btn cancel-payment-btn" type="button">Cancel</button>
      <button class="btn btn-danger delete-payment-btn" type="button">Delete</button>
    </td>
  </tr>`.firstElementChild;
  if (!row) throw new Error("payment row template produced no element");

  const dateDisplay = row.querySelector<HTMLElement>(".payment-date-display");
  const amountDisplay = row.querySelector<HTMLElement>(".payment-amount-display");
  const methodDisplay = row.querySelector<HTMLElement>(".payment-method-display");
  const referenceDisplay = row.querySelector<HTMLElement>(".payment-reference-display");
  const editFieldEls = row.querySelectorAll<HTMLElement>(".payment-edit-field");
  const editDateInput = row.querySelector<HTMLInputElement>(".payment-edit-date");
  const editAmountInput = row.querySelector<HTMLInputElement>(".payment-edit-amount");
  const editMethodSelect = row.querySelector<HTMLSelectElement>(".payment-edit-method");
  const editReferenceInput = row.querySelector<HTMLInputElement>(".payment-edit-reference");
  const editBtn = row.querySelector<HTMLButtonElement>(".edit-payment-btn");
  const saveBtn = row.querySelector<HTMLButtonElement>(".save-payment-btn");
  const cancelBtn = row.querySelector<HTMLButtonElement>(".cancel-payment-btn");
  const deleteBtn = row.querySelector<HTMLButtonElement>(".delete-payment-btn");

  if (
    !dateDisplay ||
    !amountDisplay ||
    !methodDisplay ||
    !referenceDisplay ||
    !editDateInput ||
    !editAmountInput ||
    !editMethodSelect ||
    !editReferenceInput ||
    !editBtn ||
    !saveBtn ||
    !cancelBtn ||
    !deleteBtn
  ) {
    throw new Error("payment row markup missing expected elements");
  }

  bindValue(editDateInput, editForm.fields.date);
  bindValue(editAmountInput, editForm.fields.amount, {
    format: (v) => v,
    parse: (v) => parseMoneyInput(v) ?? v,
  });
  bindSelect(editMethodSelect, editForm.fields.method);
  bindValue(editReferenceInput, editForm.fields.reference);

  bindShow(dateDisplay, notEditing);
  bindShow(amountDisplay, notEditing);
  bindShow(methodDisplay, notEditing);
  bindShow(referenceDisplay, notEditing);
  for (const el of editFieldEls) bindShow(el, editing);
  bindShow(editBtn, notEditing);
  bindShow(saveBtn, editing);
  bindShow(cancelBtn, editing);

  editBtn.addEventListener("click", startEdit);
  cancelBtn.addEventListener("click", cancelEdit);
  saveBtn.addEventListener("click", () => {
    void saveEdit();
  });
  deleteBtn.addEventListener("click", () => {
    void doDelete();
  });

  return row;
}

export function issuedInvoiceView(params: Record<string, string>): Node {
  const invoiceId = params.id ?? "";
  const invoice = signal<Invoice | null>(null);
  const lines = signal<Invoice["lines"]>([]);
  const payments = signal<Payment[]>([]);
  const errorMessage = signal("");

  breadcrumb.value = { label: "Invoices", path: "/invoices" };
  currentScope().cleanup(() => {
    breadcrumb.value = null;
  });

  function setInvoice(inv: Invoice): void {
    invoice.value = inv;
    lines.value = inv.lines;
    payments.value = inv.payments;
  }

  function reload(): void {
    getInvoice(invoiceId)
      .then((inv) => {
        if (inv.status === "draft") {
          location.assign(`/invoices/${inv.id}`);
          return;
        }
        setInvoice(inv);
      })
      .catch((err: unknown) => {
        errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }
  reload();
  reloadEmails();
  reloadReminderEligibility();

  const paymentForm = form({
    date: new Date().toISOString().slice(0, 10),
    amount: "",
    method: "bank_transfer" as const,
    reference: "",
  });

  async function addPayment(event: Event): Promise<void> {
    event.preventDefault();
    if (!invoice.value) return;
    if (!paymentForm.fields.amount.value.trim()) {
      toast.error("Amount is required.");
      return;
    }
    try {
      const updated = await createPayment(invoice.value.id, {
        date: paymentForm.fields.date.value,
        amount: paymentForm.fields.amount.value,
        method: paymentForm.fields.method.value,
        reference: paymentForm.fields.reference.value || null,
      });
      setInvoice(updated);
      paymentForm.reset({
        date: paymentForm.fields.date.value,
        amount: "",
        method: "bank_transfer",
        reference: "",
      });
      toast.success("Payment recorded.");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Failed to record payment");
    }
  }

  async function doVoid(): Promise<void> {
    if (!invoice.value) return;
    const reason = window.prompt("Reason for voiding this invoice?");
    if (!reason) return;
    try {
      const updated = await voidInvoice(invoice.value.id, reason);
      setInvoice(updated);
      toast.success("Invoice voided.");
      if (updated.closed_year_warning) toast.warning(updated.closed_year_warning);
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Void failed");
    }
  }

  async function doDuplicate(): Promise<void> {
    if (!invoice.value) return;
    try {
      const created = await duplicateInvoice(invoice.value.id);
      toast.queueSuccess("Duplicated as a new draft.");
      location.assign(`/invoices/${created.id}`);
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Duplicate failed");
    }
  }

  // "Send" preview modal (05 § Email via Fastmail): subject/body fields are only sent
  // as overrides when the user actually edits them — left untouched, the request omits
  // them so the server's own template (nicer HTML, amount/due-date table) is used
  // rather than this modal's plainer client-side approximation becoming the real email.
  const emails = signal<EmailLog[]>([]);
  const sendDialogOpen = signal(false);
  const sendError = signal("");
  const sendTo = signal("");
  const sendCc = signal("");
  const sendSubject = signal("");
  const sendBody = signal("");
  let subjectTouched = false;
  let bodyTouched = false;
  let sendIdempotencyKey = "";

  function reloadEmails(): void {
    // Uses the route's invoiceId, not invoice.value.id — called immediately on mount,
    // before the async reload() above has populated invoice.value.
    listInvoiceEmails(invoiceId)
      .then((result) => {
        emails.value = result;
      })
      .catch(() => {
        // Email history failed to load; the panel just stays empty — non-fatal.
      });
  }

  async function openSendDialog(): Promise<void> {
    if (!invoice.value) return;
    sendError.value = "";
    subjectTouched = false;
    bodyTouched = false;
    sendIdempotencyKey =
      typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `${Date.now()}`;
    sendSubject.value = "";
    sendBody.value = "";
    sendTo.value = "";
    sendCc.value = "";
    try {
      const [client, company] = await Promise.all([
        getClient(invoice.value.client_id),
        session.companyId.value ? getCompany(session.companyId.value) : Promise.resolve(null),
      ]);
      sendTo.value = client.email ?? "";
      sendCc.value = client.cc_emails.join(", ");
      const companyName = company?.trading_name || company?.name || "";
      sendSubject.value = `Invoice ${invoice.value.number ?? ""} from ${companyName}`;
      const greeting = client.contact_name ? `Hi ${client.contact_name},` : "Hi,";
      sendBody.value = `${greeting}\n\nPlease find attached invoice ${invoice.value.number ?? ""}.\n\nThanks,\n${companyName}`;
    } catch {
      // Prefill failed to load — the dialog still opens; the fields just start blank
      // and the server falls back to its own defaults for anything left empty.
    }
    sendDialogOpen.value = true;
  }

  function closeSendDialog(): void {
    sendDialogOpen.value = false;
  }

  // --- "Send reminder" modal (05 § Email: overdue reminders, manual trigger first).
  // Unlike the send modal above, the prefill is the server's *real* rendered subject and
  // body, fetched from GET /reminder — so what's on screen is what goes out. The
  // touched-flags still matter for the same reason as above: echoing an untouched body
  // back would take the override path and lose the nicer HTML template. ---
  const reminder = signal<ReminderEligibility | null>(null);
  const reminderDialogOpen = signal(false);
  const reminderError = signal("");
  const reminderTo = signal("");
  const reminderCc = signal("");
  const reminderSubject = signal("");
  const reminderBody = signal("");
  const reminderSending = signal(false);
  let reminderSubjectTouched = false;
  let reminderBodyTouched = false;
  let reminderIdempotencyKey = "";

  function reloadReminderEligibility(): void {
    getReminderEligibility(invoiceId)
      .then((result) => {
        reminder.value = result;
      })
      .catch(() => {
        reminder.value = null;
      });
  }

  async function openReminderDialog(): Promise<void> {
    if (!invoice.value) return;
    reminderError.value = "";
    reminderSubjectTouched = false;
    reminderBodyTouched = false;
    reminderIdempotencyKey =
      typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `${Date.now()}`;
    const eligibility = reminder.value;
    reminderSubject.value = eligibility?.subject ?? "";
    reminderBody.value = eligibility?.body ?? "";
    reminderTo.value = "";
    reminderCc.value = "";
    try {
      const client = await getClient(invoice.value.client_id);
      reminderTo.value = client.email ?? "";
      reminderCc.value = client.cc_emails.join(", ");
    } catch {
      // Recipients failed to load — the dialog still opens and the server falls back to
      // the client's own address for anything left blank.
    }
    reminderDialogOpen.value = true;
  }

  function closeReminderDialog(): void {
    reminderDialogOpen.value = false;
  }

  async function confirmReminder(): Promise<void> {
    if (!invoice.value) return;
    reminderError.value = "";
    reminderSending.value = true;
    try {
      const result = await sendInvoiceReminder(
        invoice.value.id,
        {
          to: splitAddresses(reminderTo.value),
          cc: splitAddresses(reminderCc.value),
          subject: reminderSubjectTouched ? reminderSubject.value : undefined,
          body: reminderBodyTouched ? reminderBody.value : undefined,
        },
        reminderIdempotencyKey,
      );
      closeReminderDialog();
      reload();
      reloadEmails();
      reloadReminderEligibility();
      if (result.status === "sent") {
        toast.success(`Reminder sent to ${result.to_addresses.join(", ")}.`);
      } else {
        toast.error(`Reminder failed: ${result.error ?? "unknown error"}`);
      }
    } catch (err) {
      const message = err instanceof ApiRequestError ? err.body.message : "Reminder failed";
      reminderError.value = message;
      toast.error(message);
    } finally {
      reminderSending.value = false;
    }
  }

  function splitAddresses(raw: string): string[] {
    return raw
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }

  async function confirmSend(): Promise<void> {
    if (!invoice.value) return;
    sendError.value = "";
    try {
      const result = await sendInvoice(
        invoice.value.id,
        {
          to: splitAddresses(sendTo.value),
          cc: splitAddresses(sendCc.value),
          subject: subjectTouched ? sendSubject.value : undefined,
          body: bodyTouched ? sendBody.value : undefined,
        },
        sendIdempotencyKey,
      );
      closeSendDialog();
      reload();
      reloadEmails();
      if (result.status === "sent") {
        toast.success(`Invoice sent to ${result.to_addresses.join(", ")}.`);
      } else {
        toast.error(`Send failed: ${result.error ?? "unknown error"}`);
      }
    } catch (err) {
      const message = err instanceof ApiRequestError ? err.body.message : "Send failed";
      sendError.value = message;
      toast.error(message);
    }
  }

  async function retrySend(emailLogId: string): Promise<void> {
    if (!invoice.value) return;
    try {
      const result = await retryInvoiceSend(invoice.value.id, emailLogId);
      reload();
      reloadEmails();
      if (result.status === "sent") {
        toast.success("Retry succeeded — invoice sent.");
      } else {
        toast.error(`Retry failed: ${result.error ?? "unknown error"}`);
      }
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Retry failed");
    }
  }

  const numberText = computed(() => invoice.value?.number ?? "");
  const statusText = computed(() => (invoice.value ? statusLabel(invoice.value.status) : ""));
  const clientNameText = computed(() => invoice.value?.client_name ?? "—");
  const issueDateText = computed(() => invoice.value?.issue_date ?? "");
  const dueDateText = computed(() => invoice.value?.due_date ?? "");
  const subtotalText = computed(() =>
    invoice.value ? formatMoney(invoice.value.subtotal, invoice.value.currency) : "",
  );
  const vatText = computed(() =>
    invoice.value ? formatMoney(invoice.value.vat_amount, invoice.value.currency) : "",
  );
  const totalText = computed(() =>
    invoice.value ? formatMoney(invoice.value.total, invoice.value.currency) : "",
  );
  const amountPaidText = computed(() =>
    invoice.value ? formatMoney(invoice.value.amount_paid, invoice.value.currency) : "",
  );
  const outstandingText = computed(() => {
    if (!invoice.value) return "";
    return formatMoney(
      subtractMoney(invoice.value.total, invoice.value.amount_paid),
      invoice.value.currency,
    );
  });
  const overpaidShow = computed(() => invoice.value?.overpaid ?? false);
  const overdueShow = computed(() => (invoice.value?.days_overdue ?? 0) > 0);
  const backdatedShow = computed(() => invoice.value?.is_backdated ?? false);
  const creditNoteShow = computed(() => invoice.value?.kind === "credit_note");
  const isVoid = computed(() => invoice.value?.status === "void");
  const voidReasonText = computed(() => invoice.value?.void_reason ?? "");
  const canVoid = computed(() => {
    const status = invoice.value?.status;
    return status !== undefined && status !== "void" && status !== "written_off";
  });

  const fragment = html`<div class="view">
    <div class="page-header">
      <h1>Invoice <span class="invoice-number"></span></h1>
      <div class="invoice-actions">
        <a class="btn download-pdf-link" target="_blank" rel="noopener">Download PDF</a>
        <button class="btn generate-pdf-btn" type="button">Generate PDF</button>
        <button class="btn upload-pdf-btn" type="button">Upload PDF</button>
        <button class="btn btn-danger remove-upload-btn" type="button">Remove uploaded PDF</button>
        <input class="pdf-upload-input" type="file" accept="application/pdf" hidden />
        <button class="btn send-invoice-btn" type="button">Send</button>
        <button class="btn send-reminder-btn" type="button">Send reminder</button>
        <button class="btn duplicate-btn" type="button">Duplicate</button>
        <button class="btn btn-danger void-btn" type="button">Void</button>
      </div>
    </div>
    <p class="form-error"></p>
    <p class="pdf-upload-note"></p>

    <div class="card vat-treatment-card">
      <div class="card-label">VAT treatment</div>
      <p>
        Currently <strong class="current-vat-treatment"></strong>. Changing this doesn't
        alter anything on the invoice — it only decides whether the supply counts towards
        flat-rate turnover.
      </p>
      <div class="form-row">
        <div class="form-field">
          <label for="issued-vat-treatment">Reclassify as</label>
          <select id="issued-vat-treatment"></select>
        </div>
        <div class="form-actions">
          <button class="btn reclassify-btn" type="button">Reclassify</button>
        </div>
      </div>
    </div>

    <dialog class="send-reminder-dialog">
      <div class="card-label">Send reminder</div>
      <p class="reminder-overdue-note field-hint"></p>
      <p class="reminder-error form-error"></p>
      <div class="form-field">
        <label for="reminder-to">To</label>
        <input id="reminder-to" placeholder="client@example.com, another@example.com" />
      </div>
      <div class="form-field">
        <label for="reminder-cc">Cc</label>
        <input id="reminder-cc" placeholder="optional" />
      </div>
      <div class="form-field">
        <label for="reminder-subject">Subject</label>
        <input id="reminder-subject" />
      </div>
      <div class="form-field">
        <label for="reminder-body">Message</label>
        <textarea id="reminder-body" rows="12"></textarea>
      </div>
      <div class="form-actions">
        <button class="btn btn-primary reminder-confirm-btn" type="button">Send reminder</button>
        <button class="btn reminder-cancel-btn" type="button">Cancel</button>
      </div>
    </dialog>

    <dialog class="send-invoice-dialog">
      <div class="card-label">Send invoice</div>
      <p class="send-error form-error"></p>
      <div class="form-field">
        <label for="send-to">To</label>
        <input id="send-to" placeholder="client@example.com, another@example.com" />
      </div>
      <div class="form-field">
        <label for="send-cc">Cc</label>
        <input id="send-cc" placeholder="optional" />
      </div>
      <div class="form-field">
        <label for="send-subject">Subject</label>
        <input id="send-subject" />
      </div>
      <div class="form-field">
        <label for="send-body">Message</label>
        <textarea id="send-body" rows="6"></textarea>
      </div>
      <div class="form-actions">
        <button class="btn btn-primary send-confirm-btn" type="button">Send</button>
        <button class="btn send-cancel-btn" type="button">Cancel</button>
      </div>
    </dialog>

    <div class="card invoice-summary-card">
      <div class="form-row">
        <div class="form-field">
          <span class="card-label">Status</span>
          <span class="badge status-badge"></span>
          <span class="badge badge-neutral credit-note-badge">Credit note</span>
          <span class="badge badge-neutral backdated-badge">Historical</span>
          <span class="badge badge-error overdue-badge">Overdue</span>
        </div>
        <div class="form-field">
          <span class="card-label">Client</span>
          <span class="client-name-display"></span>
        </div>
        <div class="form-field">
          <span class="card-label">Issue date</span>
          <span class="issue-date-display"></span>
        </div>
        <div class="form-field">
          <span class="card-label">Due date</span>
          <span class="due-date-display"></span>
        </div>
      </div>
      <p class="void-reason-display"></p>
    </div>

    <div class="card timesheet-card">
      <div class="page-header">
        <span class="card-label">Attached timesheet</span>
        <div class="invoice-actions">
          <a class="btn download-timesheet-pdf-link" target="_blank" rel="noopener">Download timesheet PDF</a>
          <button class="btn generate-timesheet-pdf-btn" type="button">Generate PDF</button>
        </div>
      </div>
      <div class="form-row">
        <div class="form-field">
          <span class="card-label">Entries</span>
          <span class="timesheet-entry-count-display"></span>
        </div>
        <div class="form-field">
          <span class="card-label">Period</span>
          <span class="timesheet-period-display"></span>
        </div>
      </div>
    </div>

    <div class="card invoice-lines-card">
      <span class="card-label">Lines</span>
      <div class="data-table">
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Qty</th>
              <th>Unit price</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody class="view-lines-body"></tbody>
        </table>
      </div>
      <table class="invoice-totals">
        <tr><td>Subtotal</td><td class="subtotal-display"></td></tr>
        <tr><td>VAT</td><td class="vat-display"></td></tr>
        <tr class="invoice-total-row"><td>Total</td><td class="total-display"></td></tr>
        <tr><td>Paid</td><td class="paid-display"></td></tr>
        <tr><td>Outstanding</td><td class="outstanding-display"></td></tr>
      </table>
      <p class="overpaid-note">This invoice has been overpaid.</p>
    </div>

    <div class="card payments-card">
      <span class="card-label">Payments</span>
      <div class="data-table">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Amount</th>
              <th>Method</th>
              <th>Reference</th>
              <th></th>
            </tr>
          </thead>
          <tbody class="payments-body"></tbody>
        </table>
      </div>

      <form class="form add-payment-form" onsubmit="${addPayment}">
        <div class="form-row">
          <div class="form-field">
            <label for="pay-date">Date</label>
            <input id="pay-date" type="date" required />
          </div>
          <div class="form-field">
            <label for="pay-amount">Amount</label>
            <input id="pay-amount" inputmode="decimal" required />
          </div>
          <div class="form-field">
            <label for="pay-method">Method</label>
            <select id="pay-method">
              <option value="bank_transfer">Bank transfer</option>
              <option value="card">Card</option>
              <option value="cash">Cash</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div class="form-field">
            <label for="pay-reference">Reference</label>
            <input id="pay-reference" />
          </div>
        </div>
        <div class="form-actions">
          <button class="btn btn-primary" type="submit">Record payment</button>
        </div>
      </form>
    </div>

    <div class="card emails-card">
      <span class="card-label">Email history</span>
      <div class="data-table">
        <table>
          <thead>
            <tr>
              <th>Sent</th>
              <th>To</th>
              <th>Subject</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody class="emails-body"></tbody>
        </table>
      </div>
      <p class="emails-empty">Not sent yet.</p>
    </div>
  </div>`;

  const numberEl = fragment.querySelector<HTMLElement>(".invoice-number");
  const downloadLink = fragment.querySelector<HTMLAnchorElement>(".download-pdf-link");
  const generatePdfBtn = fragment.querySelector<HTMLButtonElement>(".generate-pdf-btn");
  const uploadPdfBtn = fragment.querySelector<HTMLButtonElement>(".upload-pdf-btn");
  const removeUploadBtn = fragment.querySelector<HTMLButtonElement>(".remove-upload-btn");
  const pdfUploadInput = fragment.querySelector<HTMLInputElement>(".pdf-upload-input");
  const pdfUploadNote = fragment.querySelector<HTMLElement>(".pdf-upload-note");
  const duplicateBtn = fragment.querySelector<HTMLButtonElement>(".duplicate-btn");
  const voidBtn = fragment.querySelector<HTMLButtonElement>(".void-btn");
  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const statusBadge = fragment.querySelector<HTMLElement>(".status-badge");
  const overdueBadge = fragment.querySelector<HTMLElement>(".overdue-badge");
  const backdatedBadge = fragment.querySelector<HTMLElement>(".backdated-badge");
  const creditNoteBadge = fragment.querySelector<HTMLElement>(".credit-note-badge");
  const clientNameEl = fragment.querySelector<HTMLElement>(".client-name-display");
  const issueDateEl = fragment.querySelector<HTMLElement>(".issue-date-display");
  const dueDateEl = fragment.querySelector<HTMLElement>(".due-date-display");
  const voidReasonEl = fragment.querySelector<HTMLParagraphElement>(".void-reason-display");
  const vatTreatmentCard = fragment.querySelector<HTMLElement>(".vat-treatment-card");
  const currentVatTreatmentEl = fragment.querySelector<HTMLElement>(".current-vat-treatment");
  const issuedVatTreatmentSelect = fragment.querySelector<HTMLSelectElement>(
    "#issued-vat-treatment",
  );
  const reclassifyBtn = fragment.querySelector<HTMLButtonElement>(".reclassify-btn");
  const timesheetCard = fragment.querySelector<HTMLElement>(".timesheet-card");
  const timesheetEntryCountEl = fragment.querySelector<HTMLElement>(".timesheet-entry-count-display");
  const timesheetPeriodEl = fragment.querySelector<HTMLElement>(".timesheet-period-display");
  const downloadTimesheetPdfLink = fragment.querySelector<HTMLAnchorElement>(
    ".download-timesheet-pdf-link",
  );
  const generateTimesheetPdfBtn = fragment.querySelector<HTMLButtonElement>(
    ".generate-timesheet-pdf-btn",
  );
  const linesTable = fragment.querySelector<HTMLTableElement>(".invoice-lines-card table");
  const linesBody = fragment.querySelector<HTMLTableSectionElement>(".view-lines-body");
  const subtotalEl = fragment.querySelector<HTMLElement>(".subtotal-display");
  const vatEl = fragment.querySelector<HTMLElement>(".vat-display");
  const totalEl = fragment.querySelector<HTMLElement>(".total-display");
  const paidEl = fragment.querySelector<HTMLElement>(".paid-display");
  const outstandingEl = fragment.querySelector<HTMLElement>(".outstanding-display");
  const overpaidNote = fragment.querySelector<HTMLParagraphElement>(".overpaid-note");
  const paymentsTable = fragment.querySelector<HTMLTableElement>(".payments-card table");
  const paymentsBody = fragment.querySelector<HTMLTableSectionElement>(".payments-body");
  const payDateInput = fragment.querySelector<HTMLInputElement>("#pay-date");
  const payAmountInput = fragment.querySelector<HTMLInputElement>("#pay-amount");
  const payMethodSelect = fragment.querySelector<HTMLSelectElement>("#pay-method");
  const payReferenceInput = fragment.querySelector<HTMLInputElement>("#pay-reference");
  const sendInvoiceBtn = fragment.querySelector<HTMLButtonElement>(".send-invoice-btn");
  const sendDialogEl = fragment.querySelector<HTMLDialogElement>(".send-invoice-dialog");
  const reminderBtn = fragment.querySelector<HTMLButtonElement>(".send-reminder-btn");
  const reminderDialogEl = fragment.querySelector<HTMLDialogElement>(".send-reminder-dialog");
  const reminderErrorEl = fragment.querySelector<HTMLParagraphElement>(".reminder-error");
  const reminderNoteEl = fragment.querySelector<HTMLElement>(".reminder-overdue-note");
  const reminderToInput = fragment.querySelector<HTMLInputElement>("#reminder-to");
  const reminderCcInput = fragment.querySelector<HTMLInputElement>("#reminder-cc");
  const reminderSubjectInput = fragment.querySelector<HTMLInputElement>("#reminder-subject");
  const reminderBodyInput = fragment.querySelector<HTMLTextAreaElement>("#reminder-body");
  const reminderConfirmBtn = fragment.querySelector<HTMLButtonElement>(".reminder-confirm-btn");
  const reminderCancelBtn = fragment.querySelector<HTMLButtonElement>(".reminder-cancel-btn");
  const sendErrorEl = fragment.querySelector<HTMLParagraphElement>(".send-error");
  const sendToInput = fragment.querySelector<HTMLInputElement>("#send-to");
  const sendCcInput = fragment.querySelector<HTMLInputElement>("#send-cc");
  const sendSubjectInput = fragment.querySelector<HTMLInputElement>("#send-subject");
  const sendBodyInput = fragment.querySelector<HTMLTextAreaElement>("#send-body");
  const sendConfirmBtn = fragment.querySelector<HTMLButtonElement>(".send-confirm-btn");
  const sendCancelBtn = fragment.querySelector<HTMLButtonElement>(".send-cancel-btn");
  const emailsTable = fragment.querySelector<HTMLTableElement>(".emails-card table");
  const emailsBody = fragment.querySelector<HTMLTableSectionElement>(".emails-body");
  const emailsEmpty = fragment.querySelector<HTMLParagraphElement>(".emails-empty");

  if (
    !numberEl ||
    !downloadLink ||
    !generatePdfBtn ||
    !uploadPdfBtn ||
    !removeUploadBtn ||
    !pdfUploadInput ||
    !pdfUploadNote ||
    !duplicateBtn ||
    !voidBtn ||
    !errorEl ||
    !statusBadge ||
    !overdueBadge ||
    !backdatedBadge ||
    !creditNoteBadge ||
    !clientNameEl ||
    !issueDateEl ||
    !dueDateEl ||
    !voidReasonEl ||
    !vatTreatmentCard ||
    !currentVatTreatmentEl ||
    !issuedVatTreatmentSelect ||
    !reclassifyBtn ||
    !timesheetCard ||
    !timesheetEntryCountEl ||
    !timesheetPeriodEl ||
    !downloadTimesheetPdfLink ||
    !generateTimesheetPdfBtn ||
    !linesTable ||
    !linesBody ||
    !subtotalEl ||
    !vatEl ||
    !totalEl ||
    !paidEl ||
    !outstandingEl ||
    !overpaidNote ||
    !paymentsTable ||
    !paymentsBody ||
    !payDateInput ||
    !payAmountInput ||
    !payMethodSelect ||
    !payReferenceInput ||
    !sendInvoiceBtn ||
    !sendDialogEl ||
    !reminderBtn ||
    !reminderDialogEl ||
    !reminderErrorEl ||
    !reminderNoteEl ||
    !reminderToInput ||
    !reminderCcInput ||
    !reminderSubjectInput ||
    !reminderBodyInput ||
    !reminderConfirmBtn ||
    !reminderCancelBtn ||
    !sendErrorEl ||
    !sendToInput ||
    !sendCcInput ||
    !sendSubjectInput ||
    !sendBodyInput ||
    !sendConfirmBtn ||
    !sendCancelBtn ||
    !emailsTable ||
    !emailsBody ||
    !emailsEmpty
  ) {
    throw new Error("issued invoice markup missing expected elements");
  }

  bindErrorText(errorEl, errorMessage);
  bindText(numberEl, numberText);
  bindText(statusBadge, statusText);
  bindText(clientNameEl, clientNameText);
  bindText(issueDateEl, issueDateText);
  bindText(dueDateEl, dueDateText);
  bindText(voidReasonEl, computed(() => (voidReasonText.value ? `Void reason: ${voidReasonText.value}` : "")));
  bindText(subtotalEl, subtotalText);
  bindText(vatEl, vatText);
  bindText(totalEl, totalText);
  bindText(paidEl, amountPaidText);
  bindText(outstandingEl, outstandingText);

  bindShow(overdueBadge, overdueShow);
  bindShow(backdatedBadge, backdatedShow);
  bindShow(creditNoteBadge, creditNoteShow);
  bindShow(voidReasonEl, isVoid);
  bindShow(overpaidNote, overpaidShow);
  bindShow(voidBtn, canVoid);

  // Only offer the moves that don't change the VAT charged — the server refuses the rest
  // (a rated invoice can't become outside-scope without altering the document), so
  // offering them would just be a button that always errors.
  const treatmentOptions = computed(() => {
    const current = invoice.value?.vat_treatment;
    if (current === undefined) return [];
    return ZERO_VAT_TREATMENTS.has(current)
      ? (["zero_rated", "exempt", "outside_scope"] as VatTreatment[])
      : (["standard", "reduced"] as VatTreatment[]);
  });

  bindShow(vatTreatmentCard, canVoid);
  currentScope().effect(() => {
    const current = invoice.value?.vat_treatment;
    currentVatTreatmentEl.textContent = current ? VAT_TREATMENT_LABELS[current] : "";
    setSelectOptions(
      issuedVatTreatmentSelect,
      treatmentOptions.value.map((t) => ({ value: t, label: VAT_TREATMENT_LABELS[t] })),
    );
    if (current) issuedVatTreatmentSelect.value = current;
  });

  reclassifyBtn.addEventListener("click", () => {
    const id = invoice.value?.id;
    if (!id) return;
    const chosen = issuedVatTreatmentSelect.value as VatTreatment;
    void setInvoiceVatTreatment(id, chosen)
      .then((updated) => {
        invoice.value = updated;
        toast.success(`Reclassified as ${VAT_TREATMENT_LABELS[chosen]}.`);
      })
      .catch((err: unknown) => {
        toast.error(err instanceof ApiRequestError ? err.body.message : "Reclassify failed");
      });
  });

  currentScope().effect(() => {
    statusBadge.className = `badge status-badge ${invoice.value ? statusBadgeClass(invoice.value.status) : ""}`;
  });

  currentScope().effect(() => {
    downloadLink.href = invoice.value ? invoicePdfUrl(invoice.value.id) : "#";
  });

  // Issue renders the PDF outside the numbering transaction (Phase 2a item 17), so a
  // failed render leaves an issued invoice without one — this button is the recovery
  // path (POST regenerate; GETs are pure now).
  const hasPdf = computed(() => Boolean(invoice.value?.pdf_path));
  // Historical invoices record documents that really went out on a previous system's
  // letterhead, so rendering one here would fabricate a plausible-looking version of a
  // real document. They get an upload instead (15-vat-package-and-invoice-attachments.md).
  const isHistorical = computed(() => invoice.value?.is_backdated === true);
  const hasUploadedPdf = computed(() => Boolean(invoice.value?.pdf_uploaded_at));
  bindShow(downloadLink, hasPdf);
  bindShow(
    generatePdfBtn,
    computed(() => invoice.value !== null && !hasPdf.value && !isHistorical.value),
  );
  bindShow(
    uploadPdfBtn,
    computed(() => invoice.value !== null && isHistorical.value && !hasPdf.value),
  );
  bindShow(removeUploadBtn, hasUploadedPdf);
  bindShow(pdfUploadNote, hasUploadedPdf);
  bindText(
    pdfUploadNote,
    computed(() => `Attached document: ${invoice.value?.pdf_uploaded_filename ?? ""}`),
  );

  uploadPdfBtn.addEventListener("click", () => {
    pdfUploadInput.click();
  });
  pdfUploadInput.addEventListener("change", () => {
    const file = pdfUploadInput.files?.[0];
    if (!file || !invoice.value) return;
    uploadInvoicePdf(invoice.value.id, file)
      .then((updated) => {
        setInvoice(updated);
        toast.success("PDF attached.");
      })
      .catch((err: unknown) => {
        toast.error(err instanceof ApiRequestError ? err.body.message : "PDF upload failed");
      })
      .finally(() => {
        // Cleared so re-picking the same file still fires change.
        pdfUploadInput.value = "";
      });
  });
  removeUploadBtn.addEventListener("click", () => {
    if (!invoice.value) return;
    if (!window.confirm("Remove the attached PDF? The invoice will have no document.")) return;
    removeInvoicePdfUpload(invoice.value.id)
      .then((updated) => {
        setInvoice(updated);
        toast.success("Attached PDF removed.");
      })
      .catch((err: unknown) => {
        toast.error(err instanceof ApiRequestError ? err.body.message : "Removing the PDF failed");
      });
  });
  generatePdfBtn.addEventListener("click", () => {
    if (!invoice.value) return;
    regenerateInvoicePdf(invoice.value.id)
      .then((updated) => {
        setInvoice(updated);
        toast.success("PDF generated.");
      })
      .catch((err: unknown) => {
        toast.error(err instanceof ApiRequestError ? err.body.message : "PDF generation failed");
      });
  });

  duplicateBtn.addEventListener("click", () => {
    void doDuplicate();
  });
  voidBtn.addEventListener("click", () => {
    void doVoid();
  });

  // Attached-timesheet panel (04-frontend.md issued view) — only present when entries
  // were attached at issue; void releases them but the panel/PDF stay for the record.
  const hasTimesheet = computed(() => (invoice.value?.timesheet_entry_count ?? 0) > 0);
  bindShow(timesheetCard, hasTimesheet);
  bindText(timesheetEntryCountEl, computed(() => String(invoice.value?.timesheet_entry_count ?? 0)));
  bindText(
    timesheetPeriodEl,
    computed(() => {
      if (!invoice.value?.timesheet_from || !invoice.value.timesheet_to) return "—";
      return `${invoice.value.timesheet_from} – ${invoice.value.timesheet_to}`;
    }),
  );
  currentScope().effect(() => {
    downloadTimesheetPdfLink.href = invoice.value ? invoiceTimesheetPdfUrl(invoice.value.id) : "#";
  });
  const hasTimesheetPdf = computed(() => Boolean(invoice.value?.timesheet_pdf_path));
  bindShow(downloadTimesheetPdfLink, hasTimesheetPdf);
  bindShow(generateTimesheetPdfBtn, computed(() => hasTimesheet.value && !hasTimesheetPdf.value));
  generateTimesheetPdfBtn.addEventListener("click", () => {
    if (!invoice.value) return;
    regenerateInvoiceTimesheetPdf(invoice.value.id)
      .then((updated) => {
        setInvoice(updated);
        toast.success("Timesheet PDF generated.");
      })
      .catch((err: unknown) => {
        toast.error(
          err instanceof ApiRequestError ? err.body.message : "Timesheet PDF generation failed",
        );
      });
  });

  bindValue(payDateInput, paymentForm.fields.date);
  bindValue(payAmountInput, paymentForm.fields.amount, {
    format: (v) => v,
    parse: (v) => parseMoneyInput(v) ?? v,
  });
  bindSelect(payMethodSelect, paymentForm.fields.method);
  bindValue(payReferenceInput, paymentForm.fields.reference);

  list(
    linesBody,
    lines,
    (line) => line.id,
    (line) => {
      const row = html`<tr>
        <td>${line.description}</td>
        <td>${line.quantity} ${line.unit}</td>
        <td>${formatMoney(line.unit_price)}</td>
        <td>${formatMoney(line.line_total)}</td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("invoice line view row produced no element");
      return labelRow(row, linesTable);
    },
  );

  list(
    paymentsBody,
    payments,
    (p) => p.id,
    (p) => labelRow(buildPaymentRow(p, setInvoice), paymentsTable),
  );

  sendInvoiceBtn.addEventListener("click", () => {
    void openSendDialog();
  });
  sendCancelBtn.addEventListener("click", closeSendDialog);
  sendConfirmBtn.addEventListener("click", () => {
    void confirmSend();
  });
  bindValue(sendToInput, sendTo);
  bindValue(sendCcInput, sendCc);
  bindValue(sendSubjectInput, sendSubject);
  sendSubjectInput.addEventListener("input", () => {
    subjectTouched = true;
  });
  bindValue(sendBodyInput, sendBody);
  sendBodyInput.addEventListener("input", () => {
    bodyTouched = true;
  });
  // The button only appears when the invoice is genuinely chaseable, and its label
  // carries how late the invoice is so the decision to chase is informed by the row
  // itself rather than needing the modal opened first.
  bindShow(
    reminderBtn,
    computed(() => reminder.value?.remindable === true),
  );
  currentScope().effect(() => {
    const eligibility = reminder.value;
    if (!eligibility?.remindable) return;
    const days = eligibility.days_overdue;
    reminderBtn.textContent = `Send reminder (${String(days)} day${days === 1 ? "" : "s"} overdue)`;
  });
  currentScope().effect(() => {
    const eligibility = reminder.value;
    reminderNoteEl.textContent = eligibility?.remindable
      ? `${formatMoney(eligibility.outstanding)} outstanding, ${String(eligibility.days_overdue)} day(s) past the due date.`
      : "";
  });
  bindErrorText(reminderErrorEl, reminderError);
  bindValue(reminderToInput, reminderTo);
  bindValue(reminderCcInput, reminderCc);
  bindValue(reminderSubjectInput, reminderSubject);
  bindValue(reminderBodyInput, reminderBody);
  reminderSubjectInput.addEventListener("input", () => {
    reminderSubjectTouched = true;
  });
  reminderBodyInput.addEventListener("input", () => {
    reminderBodyTouched = true;
  });
  reminderBtn.addEventListener("click", () => {
    void openReminderDialog();
  });
  reminderConfirmBtn.addEventListener("click", () => {
    void confirmReminder();
  });
  reminderCancelBtn.addEventListener("click", closeReminderDialog);
  currentScope().effect(() => {
    reminderConfirmBtn.disabled = reminderSending.value;
  });
  reminderDialogEl.addEventListener("close", () => {
    reminderDialogOpen.value = false;
  });
  currentScope().effect(() => {
    if (reminderDialogOpen.value && !reminderDialogEl.open) reminderDialogEl.showModal();
    if (!reminderDialogOpen.value && reminderDialogEl.open) reminderDialogEl.close();
  });

  sendDialogEl.addEventListener("close", () => {
    sendDialogOpen.value = false;
  });
  bindErrorText(sendErrorEl, sendError);
  currentScope().effect(() => {
    if (sendDialogOpen.value && !sendDialogEl.open) sendDialogEl.showModal();
    if (!sendDialogOpen.value && sendDialogEl.open) sendDialogEl.close();
  });

  const hasEmails = computed(() => emails.value.length > 0);
  bindShow(emailsEmpty, computed(() => !hasEmails.value));
  list(
    emailsBody,
    emails,
    (e) => e.id,
    (e) =>
      labelRow(
        buildEmailRow(e, (id) => {
          void retrySend(id);
        }),
        emailsTable,
      ),
  );

  return fragment;
}
